/**
 * workshopCreateSeries — the recurring-series half of the Schedule step: the
 * draft → payload mapping, the field-level checks, and the list of dates a
 * cadence produces, with any the operator changed by hand. The generator and
 * the series rules come from `@bonsai/shared`, so the list starts as exactly
 * what the API would create.
 */
import {
  cadenceLabel,
  generateSeriesOccurrences,
  MAX_SERIES_OCCURRENCES,
  MIN_SERIES_OCCURRENCES,
  seriesAnchorFor,
  seriesEndProblem,
  type WorkshopCadence,
  type WorkshopRecurrence,
} from '@bonsai/shared';

import type { WorkshopDraftSchedule } from './useWorkshopCreateForm';
import { isPastDate, PAST_DATE_MESSAGE } from './workshopLeadTime';
import { deriveEndTime, minutesBetween, PAST_MIDNIGHT_MESSAGE } from './workshopTypeDefaults';
import { toIso, toWallClock } from './workshopWallClock';

export type RepeatChoice = 'none' | WorkshopCadence;
export type SeriesEndMode = 'until' | 'count';

/** One series date the operator changed by hand. */
export interface SeriesEdit {
  /** YYYY-MM-DD. */
  date: string;
  /** HH:MM. */
  start: string;
}

/** Hand edits keyed by the occurrence's position in the generated list. */
export type SeriesEdits = Record<number, SeriesEdit>;

export const REPEAT_OPTIONS: ReadonlyArray<{ value: RepeatChoice; label: string }> = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly_nth_weekday', label: 'Monthly, same weekday' },
];

export const END_DATE_REQUIRED = 'Pick an end date.';
export const COUNT_RANGE_MESSAGE = `Enter ${MIN_SERIES_OCCURRENCES}–${MAX_SERIES_OCCURRENCES} workshops.`;
export const SERIES_DATES_MESSAGE = 'Fix the highlighted dates in the series.';

/** The `recurrence` block the API wants, or null for a one-off. */
export function recurrenceFromSchedule(s: WorkshopDraftSchedule): WorkshopRecurrence | null {
  if (s.repeat === 'none') return null;
  if (s.endMode === 'until') return { cadence: s.repeat, untilDate: s.untilDate };
  return { cadence: s.repeat, occurrenceCount: Number(s.occurrenceCount) };
}

/** The end-condition fields' own errors, keyed to the inputs that render them. */
export function recurrenceFieldErrors(s: WorkshopDraftSchedule): Record<string, string> {
  if (s.repeat === 'none') return {};
  if (s.endMode === 'until') return s.untilDate ? {} : { untilDate: END_DATE_REQUIRED };
  const count = Number(s.occurrenceCount);
  const inRange =
    Number.isInteger(count) && count >= MIN_SERIES_OCCURRENCES && count <= MAX_SERIES_OCCURRENCES;
  return s.occurrenceCount.trim() !== '' && inRange ? {} : { occurrenceCount: COUNT_RANGE_MESSAGE };
}

/** The recurrence and first occurrence to generate from, or null while the
 *  schedule is not filled in enough to step from. */
function generationInputs(s: WorkshopDraftSchedule) {
  const recurrence = recurrenceFromSchedule(s);
  const first = toWallClock(s.date, s.start);
  if (!recurrence || !first) return null;
  if (Object.keys(recurrenceFieldErrors(s)).length > 0) return null;
  return { recurrence, first };
}

/** Why the series as a whole cannot be created, once its fields are filled in. */
export function seriesProblem(s: WorkshopDraftSchedule): string | null {
  const inputs = generationInputs(s);
  return inputs ? seriesEndProblem(inputs.first, inputs.recurrence) : null;
}

/** The generated dates, before any hand edits, as date + start pairs. */
export function generatedOccurrences(s: WorkshopDraftSchedule): SeriesEdit[] {
  const inputs = generationInputs(s);
  if (!inputs || seriesEndProblem(inputs.first, inputs.recurrence)) return [];
  return generateSeriesOccurrences(inputs.first, inputs.recurrence).map((d) => ({
    date: d.toISOString().slice(0, 10),
    start: d.toISOString().slice(11, 16),
  }));
}

export interface SeriesOccurrence extends SeriesEdit {
  /** Position in the generated list, 0-based. */
  index: number;
  /** Changed by hand from what the cadence produced. */
  edited: boolean;
  /** Why this date cannot be created, or null. */
  problem: string | null;
}

/** Why one row cannot be created: incomplete, in the past, running past
 *  midnight at the workshop's length, or a repeat of an earlier row. */
function rowProblem(
  row: SeriesEdit,
  earlier: SeriesEdit[],
  durationMinutes: number | null,
): string | null {
  if (!row.date || !row.start) return 'Enter a date and start time.';
  if (isPastDate(row.date)) return PAST_DATE_MESSAGE;
  if (durationMinutes !== null && deriveEndTime(row.start, durationMinutes) === null) {
    return PAST_MIDNIGHT_MESSAGE;
  }
  const twin = earlier.findIndex((e) => e.date === row.date && e.start === row.start);
  return twin === -1 ? null : `Same date and time as workshop ${twin + 1}.`;
}

/** The generated dates with the operator's edits applied, each checked. */
export function seriesOccurrences(s: WorkshopDraftSchedule): SeriesOccurrence[] {
  const rows = generatedOccurrences(s).map((base, index) => {
    const edit = s.seriesEdits[index];
    return { index, ...(edit ?? base), edited: edit !== undefined };
  });
  const duration = minutesBetween(s.start, s.end);
  return rows.map((row, i) => ({ ...row, problem: rowProblem(row, rows.slice(0, i), duration) }));
}

/** The start of every occurrence for the API, or undefined when nothing was
 *  edited and the server's own generator gives the same list. */
export function occurrenceStartsFor(s: WorkshopDraftSchedule): string[] | undefined {
  if (Object.keys(s.seriesEdits).length === 0) return undefined;
  return seriesOccurrences(s).map((o) => toIso(o.date, o.start));
}

/** The edits after setting one field of one row. An edit that lands back on
 *  the generated value is dropped, so the row reads as unedited again. */
export function withOccurrenceEdit(
  s: WorkshopDraftSchedule,
  index: number,
  field: keyof SeriesEdit,
  value: string,
): SeriesEdits {
  const base = generatedOccurrences(s)[index];
  if (!base) return s.seriesEdits;
  const next = { ...(s.seriesEdits[index] ?? base), [field]: value };
  const rest = { ...s.seriesEdits };
  delete rest[index];
  const unchanged = next.date === base.date && next.start === base.start;
  return unchanged ? rest : { ...rest, [index]: next };
}

export interface SeriesPreview {
  /** "Every 2 weeks on Thursdays". */
  label: string;
  occurrences: SeriesOccurrence[];
  count: number;
  /** How many dates were changed by hand. */
  editedCount: number;
  /** Why the series as stated cannot be created, or null. */
  problem: string | null;
}

/** What the cadence controls produce, or null until the schedule is filled in
 *  enough to step from: a first date, a repeat, and an end condition. (A
 *  missing end condition is the field's own error, not a problem with the
 *  series.) */
export function seriesPreviewFor(s: WorkshopDraftSchedule): SeriesPreview | null {
  const inputs = generationInputs(s);
  if (!inputs) return null;
  const label = cadenceLabel(
    inputs.recurrence.cadence,
    seriesAnchorFor(inputs.first, inputs.recurrence.cadence),
  );
  const problem = seriesEndProblem(inputs.first, inputs.recurrence);
  if (problem) return { label, occurrences: [], count: 0, editedCount: 0, problem };
  const occurrences = seriesOccurrences(s);
  const editedCount = occurrences.filter((o) => o.edited).length;
  return { label, occurrences, count: occurrences.length, editedCount, problem: null };
}

/** The series-dates error for the step, when any row has a problem. */
export function seriesDatesError(s: WorkshopDraftSchedule): string | null {
  return seriesOccurrences(s).some((o) => o.problem) ? SERIES_DATES_MESSAGE : null;
}
